import { z } from 'zod';

export const PolicyCategorySchema = z.enum([
  'SANCTIONS',
  'PEP_SCREENING',
  'ADVERSE_MEDIA',
  'JURISDICTION_RISK',
  'SOCIAL',
]);
export const PolicyImpactSchema = z.enum(['NONE', 'MEDIUM', 'HIGH']);
export const PolicyProfileSchema = z
  .object({
    categories: z.record(
      PolicyCategorySchema,
      z.object({ enabled: z.boolean(), impact: PolicyImpactSchema }),
    ),
    seriousFinancialCrime: PolicyImpactSchema,
    discrepancy: PolicyImpactSchema,
  })
  .refine((profile) => Object.values(profile.categories).some((category) => category.enabled), {
    message: 'Enable at least one screening category for every profile.',
    path: ['categories'],
  });
export const OrganizationPolicySchema = z.discriminatedUnion('reportType', [
  z.object({
    schemaVersion: z.literal(1),
    reportType: z.literal('KYC'),
    individual: PolicyProfileSchema,
  }),
  z.object({
    schemaVersion: z.literal(1),
    reportType: z.literal('KYB'),
    company: PolicyProfileSchema,
    member: PolicyProfileSchema,
  }),
]);
export const PolicySnapshotSchema = z.object({
  source: z.enum(['RAVEN_DEFAULT', 'ORGANIZATION']),
  version: z.string(),
  policy: OrganizationPolicySchema,
});
export const PublishedPolicySchema = z.object({
  version: z.string(),
  policy: OrganizationPolicySchema,
  publishedBy: z.string(),
  publishedAt: z.date(),
});
export type PolicyCategory = z.infer<typeof PolicyCategorySchema>;
export type PolicyImpact = z.infer<typeof PolicyImpactSchema>;
export type PolicyProfile = z.infer<typeof PolicyProfileSchema>;
export type OrganizationPolicy = z.infer<typeof OrganizationPolicySchema>;
export type PolicySnapshot = z.infer<typeof PolicySnapshotSchema>;
export type PublishedPolicy = z.infer<typeof PublishedPolicySchema>;

const defaultProfile = (): PolicyProfile => ({
  categories: {
    SANCTIONS: { enabled: true, impact: 'HIGH' },
    PEP_SCREENING: { enabled: true, impact: 'HIGH' },
    ADVERSE_MEDIA: { enabled: true, impact: 'MEDIUM' },
    JURISDICTION_RISK: { enabled: true, impact: 'HIGH' },
    SOCIAL: { enabled: true, impact: 'MEDIUM' },
  },
  seriousFinancialCrime: 'HIGH',
  discrepancy: 'NONE',
});
export const defaultOrganizationPolicy = (reportType: 'KYC' | 'KYB'): OrganizationPolicy =>
  reportType === 'KYC'
    ? { schemaVersion: 1, reportType, individual: defaultProfile() }
    : { schemaVersion: 1, reportType, company: defaultProfile(), member: defaultProfile() };
export const defaultPolicySnapshot = (reportType: 'KYC' | 'KYB'): PolicySnapshot => ({
  source: 'RAVEN_DEFAULT',
  version: 'raven-default-2',
  policy: defaultOrganizationPolicy(reportType),
});
export const getPolicyProfile = (
  policy: OrganizationPolicy,
  kind: 'individual' | 'company' | 'member',
): PolicyProfile =>
  policy.reportType === 'KYC'
    ? policy.individual
    : kind === 'member'
      ? policy.member
      : policy.company;

/** Stable rule identifiers are saved with signals; only application code assigns risk. */
export const evaluatePolicyFinding = (
  profile: PolicyProfile,
  finding: {
    type: string;
    classification?: string | null;
    seriousFinancialCrime: boolean;
  },
): { included: boolean; severity: PolicyImpact; appliedRule: string } => {
  const category = PolicyCategorySchema.safeParse(finding.type);
  if (!category.success || !profile.categories[category.data].enabled) {
    return { included: false, severity: 'NONE', appliedRule: 'disabled' };
  }
  if (!finding.classification || finding.classification === 'INFORMATIONAL') {
    return { included: true, severity: 'NONE', appliedRule: 'informational' };
  }
  if (finding.classification === 'DISCREPANCY') {
    return { included: true, severity: profile.discrepancy, appliedRule: 'discrepancy' };
  }
  if (finding.type === 'ADVERSE_MEDIA' && finding.seriousFinancialCrime) {
    return {
      included: true,
      severity: profile.seriousFinancialCrime,
      appliedRule: 'seriousFinancialCrime',
    };
  }
  return {
    included: true,
    severity: profile.categories[category.data].impact,
    appliedRule: `category.${category.data}`,
  };
};
